import {LockReason} from "./auth/Locked";


import type {CropModalProps} from "./CropModal"
import type {UserDeleteModalProps} from "./components/UserDeleteModal";
import type {AddMembersWorkspaceModalProps} from "./AddMembersWorkspace";
import type {RemoveUserFromWorkspaceProps} from "./RemoveUserFromWorkspace";
import type {Workspace} from "../../../API";
import type {RemoveAcknowledgmentRequireProps} from "./RemoveAcknowledgmentRequire/RemoveAcknowledgmentRequire";
import type {CopyToForTaskModalProps} from "./CopyToForTaskModal";
import type {LightBoxModalProps} from "./LightBoxModal/LightBoxModal";
import type {PaymentGateway} from "../../../checkout/types";

import type {YearCalendarModalProps} from "./YearCalendar";

import type {TaskListUnitModelType} from "@stores/taskLists/TaskListUnitModel";
import type {TaskListModelType} from "@stores/taskLists/TaskListModel";
import type {CalendarTable} from "../Lists/components/CalendarTable";
import type {RoleSummary} from "@stores/rbac/AccessRolesController";
import type {MemberRolesEditProps} from "./MemberRolesEdit/MemberRolesEdit";
import type {ItemModelType, WorkspaceModelType} from "@stores/WorkspaceStore";

import type {ExceptionData, TaskSchedule} from "@stores/taskLists/TaskSchedule";
import type {Task, TaskReport, TaskListUnit} from "../../../API";


export const MODALS = {
    CREATE_WORKSPACE: 'create_workspace',
    DELETE_WORKSPACE: 'delete_workspace',
    ADD_MEMBERS_WORKSPACE: 'addMembers_workspace',
    REMOVE_USER_FROM_WORKSPACE: 'remove_user_from_workspace',
    INVITE: 'invite',
    SIGN_UP: 'sign_up',
    LOGIN: 'login',
    FORGOT_PASSWORD: 'forgot_password',
    FILL_INFO: 'fill_info',
    CHECKOUT: 'checkout',
    PRICE_BREAKDOWN: 'price-breakdown',
    USER_DELETE: 'user_delete',
    LOCKED: 'locked',
    IMAGE_PREVIEW: 'image-preview',
    SIGN_UP_BY_LINK: 'sign-up-by-link',
    UNSAVED_CHANGE_CONFIRM: 'unsaved-change-confirm',
    UNSAVED_CHANGE_QUIZ_TAKE_CONFIRM: 'unsaved-change-quiz-take-confirm',
    IMPORT_DOC: 'import-doc',
    RESTORE_SNAPSHOT: 'restore-snapshot',
    QUIZ_AI: 'ai-quiz',
    CROP: 'crop',
    LIGHTBOX: 'lightbox',
    REMOVE_ACKNOWLEDGMENT_REQUIRE: 'remove-acknowledgment-require',
    DOCUMENT_VIEW: 'document-view',
    TASKLIST_UNIT_EDIT: 'task-list-unit-edit',
    TASKLIST_EDIT: 'task-list-edit',
    TASK_EDIT: 'task-edit',
    TASK_REPORT_EDIT: 'task-report-edit',
    TASK_REPORT_VIEW_ONLY: 'task-report-view-only',
    TASKLIST_UNIT_DELETE: 'task-list-unit-delete',
    TASK_LIST_DELETE: 'task-list-delete',
    TASK_DELETE: 'task-delete',
    COPY_TO_FOR_TASK: 'copy-to-for-task',
    YEAR_CALENDAR: 'year-calendar',
    EXCEPTIONS_CALENDAR: 'exceptions-calendar',
    ROLE_EDIT: 'role-edit',
    MEMBER_ROLES_EDIT: 'member-roles-edit',
    LIBRARY_EDIT: 'library-edit',
    SECTION_DELETE: 'section-delete',
    ASSIGN_TEST_ROLES: 'assign-test-roles',
    ASSIGN_SECTION_ROLES: 'assign-section-roles',
    ASSIGN_TASK_LIST_UNIT_ROLES: 'assign-task-list-unit-roles',
} as const;

export type ModalName = typeof MODALS[keyof typeof MODALS];

export interface ImagePopupParams {
    s3FileName: string;
}

export interface RestoreSnapshotModelParams {
    snapshotTimestamp: number;
    docId: string;
}

export interface LockedParams {
    reason: LockReason;
}

export interface SignUpParams {
    step?: number;
}

export interface CheckoutModalParams {
    enableDiscounts?: boolean;
    discounts?: boolean;
}

export interface UnsavedChangesConfirmParams {
    openPageByNameArguments: any;
}

export interface RelatedPageParams {
    docId?: string;
}

export interface DeleteWorkspaceProps {
    wsDB: Workspace;
}


export interface TaskListUnitEditProps {
    taskListUnitModel?: TaskListUnitModelType;
    calendarTable: CalendarTable;
}

export interface TaskListEditProps {
    taskListUnitModel: TaskListUnitModelType;
    taskListId?: string; // if passed we edit taskList with this Id otherwise we create new
}

export interface TaskListDeleteProps {
    taskListModel: TaskListModelType,
    taskListUnitModel: TaskListUnitModelType,
}

export interface TaskListUnitDeleteProps {
    taskListUnit: TaskListUnit;
}

export interface TaskEditProps {
    calendarTable: CalendarTable;
    taskListUnitModel: TaskListUnitModelType;
    taskListModel?: TaskListModelType;
    docId?: string;
    taskId?: string;
}

export interface TaskDeleteProps {
    task: Task,
    taskListModel?: TaskListModelType,
    taskListUnitModel: TaskListUnitModelType,
}

export interface TaskReportEditProps {
    calendarTable: CalendarTable;
    taskId: string;
    reportForDay?: TaskReport;
}

export interface TaskReportViewOnlyProps {
    calendarTable: CalendarTable;
    reportForDay: TaskReport;
}


export interface ExceptionsCalendarModalProps {
    schedule: TaskSchedule;
    onConfirm: (newVal: Map<string, ExceptionData>) => void;
    initialYear?: number;
    today?: Date;
}

export interface DocumentViewModalProps {
    task: Task;
    calendarTable: CalendarTable;
}

export interface RoleEditProps {
    roleId?: string;
    roleSummary?: RoleSummary;
}

export interface SectionDeleteProps {
    section: ItemModelType;
    workspace: WorkspaceModelType;
}

export interface AssignTestRolesProps {
    testId: string;
    testName: string;
}

export interface LibraryEditProps {
    workspace?: WorkspaceModelType;
    libraryId?: string;
}

export interface AssignSectionRolesProps {
    sectionId: string;
    sectionName: string;
}

export interface AssignTaskListUnitRolesProps {
    taskListUnitId: string;
    taskListUnitName: string;
}

export interface PriceBreakdownModalParams {
    paymentGateway: PaymentGateway;
}


export type PopupParamsMap = {
    'create_workspace': undefined;
    'delete_workspace': DeleteWorkspaceProps;
    'addMembers_workspace': AddMembersWorkspaceModalProps;
    'remove_user_from_workspace': RemoveUserFromWorkspaceProps;
    'invite': undefined;
    'sign_up': SignUpParams;
    'login': undefined;
    'forgot_password': undefined;
    'fill_info': undefined;
    'checkout': CheckoutModalParams | undefined;
    'price-breakdown': PriceBreakdownModalParams;
    'user_delete': UserDeleteModalProps;
    'locked': LockedParams;
    'image-preview': ImagePopupParams;
    'sign-up-by-link': undefined;
    'unsaved-change-confirm': UnsavedChangesConfirmParams;
    'unsaved-change-quiz-take-confirm': UnsavedChangesConfirmParams;
    'import-doc': undefined;
    'restore-snapshot': RestoreSnapshotModelParams;
    'ai-quiz': RelatedPageParams;
    'crop': CropModalProps;
    'lightbox': LightBoxModalProps;
    'remove-acknowledgment-require': RemoveAcknowledgmentRequireProps;
    'task-list-unit-edit': TaskListUnitEditProps;
    'task-list-edit': TaskListEditProps;
    'task-edit': TaskEditProps;
    'task-report-edit': TaskReportEditProps;
    'task-list-unit-delete': TaskListUnitDeleteProps;
    'task-list-delete': TaskListDeleteProps;
    'task-delete': TaskDeleteProps;
    'copy-to-for-task': CopyToForTaskModalProps;
    'year-calendar' : YearCalendarModalProps;
    'exceptions-calendar' : ExceptionsCalendarModalProps;
    'document-view': DocumentViewModalProps;
    'task-report-view-only': TaskReportViewOnlyProps;
    'role-edit': RoleEditProps;
    'member-roles-edit': MemberRolesEditProps;
    'library-edit': LibraryEditProps;
    'section-delete': SectionDeleteProps;
    'assign-test-roles': AssignTestRolesProps;
    'assign-section-roles': AssignSectionRolesProps;
    'assign-task-list-unit-roles': AssignTaskListUnitRolesProps;
};
